import {
  type FocusEventHandler,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import type { DateRange } from "react-day-picker";
import {
  endOfDay,
  format,
  isBefore,
  isValid,
  startOfDay,
  subMonths,
} from "date-fns";
import { Calendar01Icon, ChevronIcon, CloseIcon } from "vega-icons";

import { DropdownButton } from "@vega-core/components/Buttons/DropdownButton/DropdownButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { Button } from "@vega-core/components/shadcn/button";
import { Calendar } from "@vega-core/components/shadcn/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { Key } from "@vega-core/constants/global";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { DateFormatString } from "@vega-core/utils/formatDate";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { MonthYearView } from "./components/MonthYearView";
import { type CalendarView } from "./hooks/types";
import { useCalendarCaption } from "./hooks/useCalendarCaption";
import { type PresetOption, PresetSelector } from "./PresetSelector";

export type { PresetOption };

export type ThemedDateRangeProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  fullWidth?: boolean;
  id?: string;
  isOptional?: boolean;
  label?: JSX.Element | string;
  labelTag?: ReactNode;
  minDate?: Date;
  onBlur?: FocusEventHandler;
  onChange: (value: DateRange) => void;
  size?: number;
  startAt?: number;
  value?: DateRange | string;
  withBorder?: boolean;
  showPresetSelector?: boolean;
  presetOptions?: PresetOption[];
  buttonPlaceholder?: string;
  buttonVariant?: "default" | "select";
  isLoading?: boolean;
  name: string;
  hideClearButton?: boolean;
  iconOnly?: boolean;
  shouldDisablePreset?: (preset: PresetOption) => boolean;
  shouldDisableDate?: (date: Date) => boolean;
};

const CALENDAR_CLASS_NAMES = {
  day_in_range: "bg-background/component text-text/primary",
  button: "w-full h-full",
} as const;

const BUTTON_CLASSES = {
  base: "h-11 first-letter:w-full justify-start text-left font-normal bg-background/primary text-text/primary hover:text-text/primary hover:bg-background/primary hover:border-brand-active active:border-brand-active",
  disabled:
    "text-text/disabled border-border/secondary hover:text-text/disabled hover:border-border/secondary cursor-not-allowed",
  border: "border-border/primary",
  active: "border-brand-active",
} as const;

const formatDateRange = (range: DateRange | undefined): string => {
  if (!range?.from || !range.to) return "";

  return `${format(range.from, DateFormatString.US_DATE_SLASH)} - ${format(range.to, DateFormatString.US_DATE_SLASH)}`;
};

const normalizeDate = (
  input: Date | number | string | null | undefined,
): Date | undefined => {
  if (input == null) return undefined;

  const date = input instanceof Date ? input : new Date(input);

  return isValid(date) ? date : undefined;
};

const parseDateRangeValue = (
  value: DateRange | string | undefined,
): DateRange | undefined => {
  if (!value) return undefined;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      if (!parsed.from || !parsed.to) return undefined;

      const fromDate = normalizeDate(parsed.from);
      const toDate = normalizeDate(parsed.to);

      if (!fromDate || !toDate) return undefined;

      return {
        from: fromDate,
        to: toDate,
      };
    } catch {
      return undefined;
    }
  }

  if (!isValid(value.from) || !isValid(value.to)) return undefined;

  return value;
};

export function ThemedDateRangeField({
  className,
  disabled,
  error,
  fullWidth = false,
  id,
  isOptional,
  label,
  labelTag,
  onChange,
  value,
  withBorder = true,
  showPresetSelector = false,
  presetOptions,
  buttonPlaceholder,
  buttonVariant = "default",
  isLoading,
  minDate,
  name,
  hideClearButton = false,
  iconOnly = false,
  shouldDisablePreset,
  shouldDisableDate,
}: ThemedDateRangeProps) {
  const isMobile = !useDeviceWidth("md");
  const [open, setOpen] = useState(false);

  const dateRange = parseDateRangeValue(value);
  const setDateRange = useCallback(
    (dateRange: DateRange | undefined) => {
      onChange(dateRange ?? { from: undefined, to: undefined });
    },
    [onChange],
  );
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>(
    parseDateRangeValue(value),
  );
  const [view, setView] = useState<CalendarView>("DAY");
  const [editingDisplayIndex, setEditingDisplayIndex] = useState<number>(0);
  const [displayMonth, setDisplayMonth] = useState<Date>(() => {
    if (value && typeof value === "object" && value.from) {
      return value.from;
    }

    return new Date();
  });

  useEffect(() => {
    const parsed = parseDateRangeValue(value);
    setTempDateRange(parsed);

    if (parsed?.from) {
      setDisplayMonth(parsed.from);
    } else if (value && typeof value === "object" && value.from) {
      setDisplayMonth(value.from);
    }
  }, [value]);

  const { renderCaption } = useCalendarCaption();

  const getDisabledDate = useCallback(
    (date: Date): boolean => {
      if (shouldDisableDate) {
        return shouldDisableDate(date);
      }

      const normalizedMinDate = normalizeDate(minDate);

      return normalizedMinDate ? isBefore(date, normalizedMinDate) : false;
    },
    [minDate, shouldDisableDate],
  );

  const handleDateRangeChange = useCallback(
    (selectedDate: DateRange | undefined) => {
      if (!selectedDate) {
        setTempDateRange(undefined);
        setDateRange(undefined);
        onChange({ from: undefined, to: undefined });
        setOpen(false);

        return;
      }

      if (
        selectedDate.from &&
        selectedDate.to &&
        selectedDate.to < selectedDate.from
      ) {
        return;
      }

      const normalizedMinDate = normalizeDate(minDate);

      if (
        normalizedMinDate &&
        selectedDate.from &&
        isBefore(selectedDate.from, normalizedMinDate)
      ) {
        return;
      }

      setTempDateRange(selectedDate);
    },
    [minDate, setDateRange, onChange],
  );

  const handlePresetSelect = useCallback(
    (selectedDate: DateRange | undefined) => {
      if (!selectedDate) {
        setTempDateRange(undefined);
        setDateRange(undefined);
        onChange({ from: undefined, to: undefined });
        setOpen(false);

        return;
      }

      const { from, to } = selectedDate;

      if (!from || !to) {
        return;
      }

      if (to < from) {
        return;
      }

      const normalizedMinDate = normalizeDate(minDate);

      if (normalizedMinDate && isBefore(from, normalizedMinDate)) {
        return;
      }

      const fromDate = startOfDay(new Date(from));
      const toDate = endOfDay(new Date(to));

      const adjustedRange = {
        from: fromDate,
        to: toDate,
      };

      setTempDateRange(adjustedRange);
      setDateRange(adjustedRange);
      onChange(adjustedRange);
      setOpen(false);
    },
    [minDate, setDateRange, onChange],
  );

  const handleApply = useCallback(() => {
    if (!tempDateRange?.from || !tempDateRange.to) return;

    const { from, to } = tempDateRange;

    const fromDate = startOfDay(new Date(from));
    const toDate = endOfDay(new Date(to));

    const normalizedMinDate = normalizeDate(minDate);

    if (
      normalizedMinDate &&
      (isBefore(fromDate, normalizedMinDate) ||
        isBefore(toDate, normalizedMinDate))
    ) {
      return;
    }

    if (getDisabledDate(fromDate) || getDisabledDate(toDate)) {
      return;
    }

    const adjustedRange = {
      from: fromDate,
      to: toDate,
    };

    setDateRange(adjustedRange);
    onChange(adjustedRange);
    setOpen(false);
  }, [tempDateRange, minDate, getDisabledDate, setDateRange, onChange]);

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setTempDateRange(undefined);
      setDateRange(undefined);
      onChange({ from: undefined, to: undefined });
      setOpen(false);
    },
    [onChange, setDateRange],
  );

  const handleMonthYearSelect = useCallback(
    (date: Date) => {
      if (!isValid(date)) return;

      const numberOfMonths = isMobile ? 1 : 2;

      if (numberOfMonths > 1 && editingDisplayIndex === numberOfMonths - 1) {
        setDisplayMonth(subMonths(date, 1));
      } else {
        setDisplayMonth(date);
      }

      if (view === "MONTH" || view === "YEAR") {
        setView("DAY");
      }
    },
    [view, isMobile, editingDisplayIndex],
  );

  const handleMonthYearNavigate = useCallback((date: Date) => {
    if (!isValid(date)) return;
    setDisplayMonth(date);
  }, []);

  const handleMonthChange = useCallback((date: Date) => {
    if (!isValid(date)) return;
    // Allow navigation to any month - dates are disabled individually by getDisabledDate
    setDisplayMonth(date);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === Key.Escape) {
      setOpen(false);
    }
  }, []);

  const hasTempRangeSet =
    tempDateRange &&
    tempDateRange.from &&
    tempDateRange.to &&
    !getDisabledDate(tempDateRange.from) &&
    !getDisabledDate(tempDateRange.to);
  const dateRangeText = formatDateRange(dateRange);
  const getSelectText = (placeholder = "Pick a date range") =>
    dateRangeText || placeholder;

  const handleEnterMonthView = useCallback(
    (displayIndex: number, captionDisplayMonth: Date) => {
      setEditingDisplayIndex(displayIndex);
      setDisplayMonth(captionDisplayMonth);
    },
    [],
  );

  const handleEnterYearView = useCallback(
    (displayIndex: number, captionDisplayMonth: Date) => {
      setEditingDisplayIndex(displayIndex);
      setDisplayMonth(captionDisplayMonth);
    },
    [],
  );

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setOpen(false);
        setTimeout(() => {
          setView("DAY");
        }, 100);
      } else if (!disabled && !isLoading) {
        setOpen(true);
      }
    },
    [disabled, isLoading],
  );

  const getClearButton = () => {
    if (hideClearButton) return null;

    return (
      <span
        aria-label="Clear date range"
        className="inline-flex items-center justify-center h-4 w-4 rounded-full text-text/secondary bg-background/tertiary transition hover:bg-background/primary cursor-pointer"
        data-testid={`${name}-clear-date-range`}
        onClick={(e) => {
          e.stopPropagation();
          handleClear(e);
        }}
        role="button"
      >
        <CloseIcon className="h-3 w-3" />
      </span>
    );
  };

  return (
    <div
      className={cnTwMerge("w-full lg:w-auto group", {
        "w-auto": !fullWidth,
      })}
      id={id}
    >
      <FormLabel isOptional={isOptional} labelElement={label} tag={labelTag} />
      <div
        className={cnTwMerge("flex gap-2 w-full lg:w-auto", {
          "w-auto": !fullWidth,
        })}
      >
        <Popover onOpenChange={handlePopoverOpenChange} open={open}>
          {buttonVariant === "select" ? (
            <PopoverTrigger
              className={cnTwMerge("w-full flex", {
                "cursor-not-allowed": disabled || isLoading,
              })}
            >
              <DropdownButton
                aria-label="Select date range"
                as="span"
                className={cnTwMerge({
                  [BUTTON_CLASSES.disabled]: disabled || isLoading,
                })}
                dataTestId={`${name}-date-trigger`}
                disabled={disabled || isLoading}
                icon={
                  <div className="flex items-center gap-1">
                    <ChevronIcon
                      className={cnTwMerge(
                        "h-4 w-4 shrink-0 text-text/secondary group-hover:text-text/interactive defaultTransition",
                        open && "rotate-180",
                      )}
                    />
                    {dateRangeText ? getClearButton() : null}
                  </div>
                }
                isLoading={isLoading}
                onClick={() => setOpen(!open)}
                open={open}
              >
                <span className="flex-1 text-left">
                  {getSelectText(buttonPlaceholder)}
                </span>
              </DropdownButton>
            </PopoverTrigger>
          ) : (
            <PopoverTrigger asChild>
              {iconOnly ? (
                <ThemedIconButton
                  ariaLabel="Select date range"
                  className={cnTwMerge({
                    [BUTTON_CLASSES.active]: open,
                  })}
                  dataTestId={`${name}-date-trigger`}
                  disabled={disabled || isLoading}
                  icon={<Calendar01Icon className="h-4 w-4" />}
                  isLoading={isLoading}
                  onClick={() => setOpen(!open)}
                  size="large"
                  variant="background"
                />
              ) : (
                <Button
                  aria-label="Select date range"
                  className={cnTwMerge(
                    "w-full flex",
                    BUTTON_CLASSES.base,
                    !dateRange && "text-muted-foreground",
                    {
                      "cursor-not-allowed": disabled || isLoading,
                      [BUTTON_CLASSES.border]:
                        isTruthy(withBorder) && !isLoading,
                      [BUTTON_CLASSES.disabled]:
                        isTruthy(withBorder) && isLoading,
                    },
                    { [BUTTON_CLASSES.active]: open },
                    fullWidth ? "w-full" : "w-min",
                    className,
                  )}
                  data-testid={`${name}-date-trigger`}
                  disabled={disabled || isLoading}
                  variant="outline"
                >
                  <div className="flex items-center w-full gap-2">
                    <div className="flex items-center flex-1">
                      <Calendar01Icon
                        aria-hidden="true"
                        className="mr-2 h-4 w-4"
                      />
                      {getSelectText()}
                    </div>
                    {dateRangeText ? getClearButton() : null}
                  </div>
                </Button>
              )}
            </PopoverTrigger>
          )}
          <PopoverContent
            className="w-auto p-0 flex flex-col md:flex-row"
            data-testid="themed-date-range-popover"
            onKeyDown={handleKeyDown}
          >
            {view === "DAY" ? (
              <>
                {showPresetSelector ? (
                  <div className="border-b md:border-b-0 md:border-r">
                    <PresetSelector
                      onSelectPreset={handlePresetSelect}
                      presets={presetOptions}
                      selectedPreset={dateRange}
                      shouldDisablePreset={shouldDisablePreset}
                    />
                  </div>
                ) : null}
                <div className="flex flex-col">
                  <Calendar
                    captionLayout="dropdown"
                    classNames={CALENDAR_CLASS_NAMES}
                    components={{
                      Caption: (props) =>
                        renderCaption(
                          {
                            ...props,
                            onClick: handleMonthChange,
                            numberOfMonths: isMobile ? 1 : 2,
                            onEnterMonthView: handleEnterMonthView,
                            onEnterYearView: handleEnterYearView,
                          },
                          view,
                          setView,
                        ),
                    }}
                    disabled={(date: Date) => {
                      if (disabled || isLoading) return true;

                      return getDisabledDate(date);
                    }}
                    fixedWeeks
                    fromDate={normalizeDate(minDate)}
                    initialFocus
                    mode="range"
                    month={displayMonth}
                    name={name}
                    numberOfMonths={isMobile ? 1 : 2}
                    onMonthChange={handleMonthChange}
                    onSelect={handleDateRangeChange}
                    selected={tempDateRange}
                    showOutsideDays={false}
                  />
                  <div className="flex justify-end gap-2 p-3 border-t">
                    <ThemedButton
                      dataTestId="clear-button"
                      onClick={handleClear}
                      variant="secondary"
                    >
                      {m.btn_clear()}
                    </ThemedButton>
                    <ThemedButton
                      dataTestId="apply-button"
                      disabled={!hasTempRangeSet}
                      isLoading={isLoading}
                      onClick={handleApply}
                    >
                      {m.btn_apply()}
                    </ThemedButton>
                  </div>
                </div>
              </>
            ) : (
              <MonthYearView
                displayMonth={displayMonth}
                minDate={minDate}
                name={name}
                onNavigate={handleMonthYearNavigate}
                onSelect={handleMonthYearSelect}
                shouldDisableDate={shouldDisableDate}
                view={view}
              />
            )}
          </PopoverContent>
        </Popover>
      </div>

      {isTruthy(error) && (
        <ValidationErrorText dataTestId={error} withMargin>
          {error}
        </ValidationErrorText>
      )}
    </div>
  );
}
